import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import TideChart from '../components/TideChart';
import { FishingTodayCard, SevenDayTides, SevenDayFishing } from '../components/tide-panels';

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', href);
}
const portSlug = (p: string) => p.toLowerCase().replace(/\s+/g, '-');

interface TideEvent { time: string; height: number; type: 'high' | 'low'; }
interface DayTides { date: string; dayName: string; tides: TideEvent[]; }
interface MoonPhase { phase: number; name: string; emoji: string; illumination: number; }
interface FishingPeriod { start: string; end: string; type: string; }
interface FishingDay {
  date: string; dayName: string; rating: number; ratingLabel: string;
  majorPeriods: FishingPeriod[]; minorPeriods: FishingPeriod[];
}
interface TideData {
  port: string; region: string; moonPhase: MoonPhase;
  tides: DayTides[]; fishingTimes: FishingDay[];
  allPorts: { name: string; region: string }[];
}

const PORTS = [
  'Auckland','Tauranga','Wellington','Lyttelton','Timaru','Port Chalmers','Bluff',
  'Westport','Greymouth','Nelson','Picton','Napier','Gisborne','New Plymouth',
  'Whangarei','Russell','Whitianga','Raglan',
];

const RATING_STARS = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
}

// Live tide countdown - shows minutes to next high tide or past last high tide
function TideCountdown({ tides }: { tides: DayTides[] }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000 * 30); // update every 30s
    return () => clearInterval(timer);
  }, []);

  if (!tides.length) return null;

  // Flatten all high tides from all days into a single sorted list
  const allHighTides: { time: string; height: number; dayName: string; date: string }[] = [];
  for (const day of tides) {
    for (const t of day.tides) {
      if (t.type === 'high') {
        allHighTides.push({ ...t, dayName: day.dayName, date: day.date });
      }
    }
  }

  // Find the next upcoming high tide, or the most recent past one
  const nowMs = now.getTime();
  let nextHigh = allHighTides.find(t => new Date(t.time).getTime() > nowMs);
  let lastHigh = null;
  if (!nextHigh) {
    // All high tides are in the past - use the last one
    lastHigh = allHighTides[allHighTides.length - 1];
  } else {
    // Find the most recent past high tide
    const pastHighs = allHighTides.filter(t => new Date(t.time).getTime() <= nowMs);
    if (pastHighs.length) lastHigh = pastHighs[pastHighs.length - 1];
  }

  const isCountdown = !!nextHigh;
  const targetTime = isCountdown ? new Date(nextHigh.time) : (lastHigh ? new Date(lastHigh.time) : null);
  if (!targetTime) return null;

  const diffMs = isCountdown
    ? targetTime.getTime() - nowMs
    : nowMs - targetTime.getTime();

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const seconds = Math.floor((diffMs % 60000) / 1000);
  const tideHeight = isCountdown ? nextHigh.height : (lastHigh?.height ?? 0);
  const tideDay = isCountdown ? nextHigh.dayName : (lastHigh?.dayName ?? '');
  const isToday = new Date(targetTime).toDateString() === new Date().toDateString();

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl p-4 mb-4 border ${
        isCountdown
          ? 'border-accent/30 bg-gradient-to-br from-accent/10 to-accent/5'
          : 'border-[rgba(79,179,217,0.15)] bg-gradient-to-br from-surface-light to-card-light dark:from-surface dark:to-card'
      }`}
    >
      {/* Animated wave background */}
      <motion.div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, transparent 50%, rgba(79,179,217,0.3) 100%)',
        }}
        animate={{ opacity: [0.05, 0.15, 0.05] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div className="relative z-10 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-text-muted text-[0.6rem] uppercase tracking-wider mb-0.5">
            {isCountdown ? '⏱️ Next High Tide' : '📉 Last High Tide was'}
          </div>
          <div className="flex items-baseline gap-1.5">
            {hours > 0 && (
              <span className="font-display text-2xl font-extrabold tabular-nums">{hours}</span>
            )}
            {hours > 0 && <span className="text-text-secondary text-sm font-bold">hr</span>}
            <span className="font-display text-2xl font-extrabold tabular-nums">{mins}</span>
            <span className="text-text-secondary text-sm font-bold">min</span>
            <span className="font-display text-lg font-bold tabular-nums text-text-secondary">:{String(seconds).padStart(2, '0')}</span>
          </div>
          <div className="text-text-secondary text-[0.7rem] mt-0.5">
            {isCountdown
              ? `${tideHeight}m · ${isToday ? 'Today' : tideDay} at ${fmtTime(targetTime.toISOString())}`
              : `${tideHeight}m · ${fmtTime(targetTime.toISOString())}`
            }
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <motion.div
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="text-3xl"
          >
            {isCountdown ? '🌊' : '📉'}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

// 3D Moon Visual
function MoonVisual({ phase, illumination }: { phase: number; illumination: number }) {
  const shadowOffset = phase < 0.5 ? -(1 - phase * 2) * 100 : (phase - 0.5) * 2 * 100;
  return (
    <div className="relative w-24 h-24 mx-auto" style={{ perspective: '400px' }}>
      <motion.div
        className="absolute inset-0 rounded-full blur-2xl"
        style={{ background: illumination > 50 ? 'rgba(255,245,200,0.3)' : 'rgba(79,179,217,0.15)' }}
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div
        className="relative w-full h-full rounded-full overflow-hidden"
        style={{
          background: 'radial-gradient(circle at 35% 30%, #f5f0e0 0%, #d4c9a8 60%, #a89a78 100%)',
          boxShadow: 'inset -8px -8px 20px rgba(0,0,0,0.3), 0 0 30px rgba(255,245,200,0.2)',
          transformStyle: 'preserve-3d',
        }}
      >
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'rgba(10,15,25,0.92)',
            clipPath: illumination >= 50
              ? `inset(0 ${(100 - illumination * 2)}% 0 ${shadowOffset}%)`
              : `inset(0 ${illumination * 2}% 0 ${shadowOffset}%)`,
          }}
        />
        <div className="absolute top-[20%] left-[25%] w-3 h-3 rounded-full bg-[rgba(0,0,0,0.08)]" />
        <div className="absolute top-[55%] left-[60%] w-4 h-4 rounded-full bg-[rgba(0,0,0,0.06)]" />
        <div className="absolute top-[70%] left-[30%] w-2 h-2 rounded-full bg-[rgba(0,0,0,0.05)]" />
      </div>
    </div>
  );
}

// 3D Tide Card
function TideCard({ tide, index }: { tide: TideEvent; index: number }) {
  const isHigh = tide.type === 'high';
  return (
    <motion.div
      initial={{ opacity: 0, rotateY: -90, x: -40 }}
      animate={{ opacity: 1, rotateY: 0, x: 0 }}
      transition={{ delay: index * 0.1, type: 'spring', stiffness: 120, damping: 16 }}
      whileTap={{ scale: 0.95 }}
      className={`relative rounded-2xl p-4 border overflow-hidden ${
        isHigh
          ? 'border-[rgba(79,179,217,0.25)] bg-gradient-to-br from-[rgba(79,179,217,0.08)] to-[rgba(79,179,217,0.02)]'
          : 'border-[rgba(79,179,217,0.08)] bg-surface-light dark:bg-surface'
      }`}
      style={{ transformStyle: 'preserve-3d', perspective: '600px' }}
    >
      {isHigh && (
        <motion.div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, transparent 60%, rgba(79,179,217,0.15) 100%)' }}
          animate={{ opacity: [0.15, 0.3, 0.15] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      <div className="relative z-10">
        <div className="text-text-muted text-[0.65rem] uppercase tracking-wider mb-1">
          {isHigh ? '🔼 High Tide' : '🔽 Low Tide'}
        </div>
        <div className="font-display text-xl font-extrabold">{fmtTime(tide.time)}</div>
        <div className="text-text-secondary text-sm font-semibold">{tide.height}m</div>
      </div>
    </motion.div>
  );
}

// Rating Badge
function RatingBadge({ rating, label }: { rating: number; label: string }) {
  const colors = ['#ef4444','#f97316','#eab308','#22c55e','#06b6d4','#a855f7'];
  const color = colors[rating] || colors[0];
  return (
    <motion.div
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-bold text-xs"
      style={{ background: `${color}20`, border: `1px solid ${color}40`, color }}
    >
      <span className="text-base">{RATING_STARS(rating)}</span>
      {label && <span>{label}</span>}
    </motion.div>
  );
}

// Port Selector
function PortSelector({ ports, selected, onSelect }: { ports: string[]; selected: string; onSelect: (p: string) => void }) {
  return (
    <div className="relative overflow-hidden lg:overflow-visible -mx-4 px-4 lg:mx-0 lg:px-0">
      <div
        className="flex gap-2 overflow-x-auto lg:overflow-visible lg:flex-wrap pb-2 lg:pb-0"
        style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}
      >
        {ports.map(port => (
          <button
            key={port}
            onClick={() => onSelect(port)}
            style={{ scrollSnapAlign: 'start' }}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
              selected === port
                ? 'bg-accent text-white dark:text-bg shadow-lg'
                : 'bg-surface-light dark:bg-surface text-text-secondary border border-[rgba(16,42,66,0.09)] hover:border-accent/40 hover:text-text-primary'
            }`}
          >
            {port}
          </button>
        ))}
      </div>
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-bg to-transparent pointer-events-none lg:hidden" />
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-bg to-transparent pointer-events-none lg:hidden" />
    </div>
  );
}

// Tab Bar with sliding indicator
function TabBar({ active, onChange }: { active: 'tides'|'fishing'|'moon'; onChange: (t: 'tides'|'fishing'|'moon') => void }) {
  const tabs: { key: 'tides'|'fishing'|'moon'; label: string; icon: string }[] = [
    { key: 'tides', label: 'Tides', icon: '🌊' },
    { key: 'fishing', label: 'Fishing', icon: '🎣' },
    { key: 'moon', label: 'Moon', icon: '🌙' },
  ];
  return (
    <div className="relative flex gap-1 p-1 bg-surface-light dark:bg-surface border border-[rgba(16,42,66,0.09)] rounded-xl">
      {tabs.map(tab => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className="relative flex-1 px-4 py-2.5 rounded-lg text-sm font-bold z-10 transition-colors"
          style={{ color: active === tab.key ? 'var(--accent)' : 'var(--text-secondary)' }}
        >
          {tab.icon} {tab.label}
          {active === tab.key && (
            <motion.div
              layoutId="tab-bg"
              className="absolute inset-0 rounded-lg bg-accent/10 border border-accent/20 -z-10"
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            />
          )}
        </button>
      ))}
    </div>
  );
}

export default function Tides() {
  const { port: portParam } = useParams();
  const navigate = useNavigate();
  const [selectedPort, setSelectedPort] = useState('Auckland');
  const [data, setData] = useState<TideData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'tides'|'fishing'|'moon'>('tides');
  const dragStartX = useRef(0);

  // URL-driven port selection: /tides/port-chalmers -> 'Port Chalmers'
  useEffect(() => {
    if (!portParam) return;
    const match = PORTS.find(p => portSlug(p) === portParam.toLowerCase());
    if (match) setSelectedPort(match);
    else navigate('/tides', { replace: true });
  }, [portParam, navigate]);

  // Per-port SEO meta
  useEffect(() => {
    document.title = `${selectedPort} Tide Times - High & Low Tides Today | MarineLink`;
    setMeta('description', `${selectedPort} tide times for today and the week ahead: high and low tide heights, tide curve, moon phase and solunar fishing times. NZ tide predictions for ${selectedPort} on MarineLink.`);
    setCanonical(`https://marinelink.co.nz/tides/${portSlug(selectedPort)}`);
    return () => { document.title = 'MarineLink - NZ Marine Industry Directory'; };
  }, [selectedPort]);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetch(`/.netlify/functions/tides?port=${encodeURIComponent(selectedPort)}&days=7`)
      .then(res => res.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(() => setError('Failed to load tide data'))
      .finally(() => setLoading(false));
  }, [selectedPort]);

  const todayTides = data?.tides[0];
  const todayFishing = data?.fishingTimes[0];

  // Swipe between tabs
  const handleTouchStart = (e: React.TouchEvent) => { dragStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = dragStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) < 50) return;
    const tabs: ('tides'|'fishing'|'moon')[] = ['tides','fishing','moon'];
    const idx = tabs.indexOf(tab);
    if (diff > 0 && idx < 2) setTab(tabs[idx + 1]);
    if (diff < 0 && idx > 0) setTab(tabs[idx - 1]);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Header */}
      <section className="relative overflow-hidden pt-6 pb-4 px-4">
        <motion.div
          className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(79,179,217,0.08), transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-20 left-0 w-64 h-64 rounded-full blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.06), transparent 70%)' }}
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="relative z-10 max-w-[500px] lg:max-w-[1200px] mx-auto text-center lg:text-left">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-2xl lg:text-4xl font-extrabold tracking-tight mb-1"
          >
            Tides & Fishing Times
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-text-secondary text-xs lg:text-sm mb-4"
          >
            NZ tide predictions, moon phases & solunar feeding times
          </motion.p>
          <PortSelector ports={PORTS} selected={selectedPort} onSelect={(p) => navigate(`/tides/${portSlug(p)}`)} />
        </div>
      </section>

      {/* Loading */}
      {loading && (
        <div className="max-w-[500px] mx-auto px-4 py-16 text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            className="text-4xl mb-3 inline-block"
          >🌊</motion.div>
          <div className="text-text-muted text-sm">Loading tide data for {selectedPort}…</div>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="max-w-[500px] mx-auto px-4 py-16 text-center">
          <div className="text-red-500 text-sm">⚠️ {error}</div>
        </div>
      )}

      {/* Content */}
      {data && !loading && (
        <div className="max-w-[500px] lg:max-w-[1200px] mx-auto px-4 md:px-8 pb-12">
          <div className="lg:hidden">
          {/* Moon phase summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-gradient-to-br from-surface-light to-card-light dark:from-surface dark:to-card border border-[rgba(16,42,66,0.09)] p-4 mb-4 flex items-center gap-4"
          >
            <MoonVisual phase={data.moonPhase.phase} illumination={data.moonPhase.illumination} />
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-bold">{data.moonPhase.name}</div>
              <div className="text-text-secondary text-xs">{data.moonPhase.illumination}% illuminated</div>
              <div className="text-text-muted text-[0.7rem] mt-1 leading-snug">
                {data.moonPhase.illumination > 90 || data.moonPhase.illumination < 10
                  ? '⭐ Peak fishing - new/full moon brings strongest feeding'
                  : '🐟 Good conditions - moderate solunar activity'}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-text-muted text-[0.6rem] uppercase">Location</div>
              <div className="font-bold text-sm">{data.port}</div>
            </div>
          </motion.div>

          {/* Tab bar */}
          <div className="mb-4">
            <TabBar active={tab} onChange={setTab} />
          </div>

          {/* Swipeable content */}
          <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} className="min-h-[300px]">
            <AnimatePresence mode="wait">
              {/* === TIDES TAB === */}
              {tab === 'tides' && todayTides && (
                <motion.div
                  key="tides"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* Tide curve chart */}
                  <TideChart days={data.tides} />

                  {/* Live countdown to next high tide */}
                  <TideCountdown tides={data.tides} />

                  {/* Today's tides - 2x2 grid */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {todayTides.tides.map((tide, i) => (
                      <TideCard key={i} tide={tide} index={i} />
                    ))}
                  </div>

                  {/* 7-day forecast */}
                  <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden mb-4">
                    <div className="bg-surface-light dark:bg-surface px-4 py-2.5 border-b border-[rgba(16,42,66,0.09)]">
                      <span className="font-display text-xs font-bold uppercase tracking-wider">7-Day Tide Forecast</span>
                    </div>
                    <div className="divide-y divide-[rgba(79,179,217,0.05)]">
                      {data.tides.map((day, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.05 }}
                          className="px-3 py-2.5 flex items-center gap-2"
                        >
                          <div className="w-14 flex-shrink-0">
                            <div className="font-semibold text-xs">{i === 0 ? 'Today' : day.dayName.slice(0, 3)}</div>
                            <div className="text-text-muted text-[0.65rem]">{fmtDate(day.date)}</div>
                          </div>
                          <div className="flex-1 flex items-end gap-1">
                            {day.tides.map((t, j) => (
                              <div key={j} className="flex-1 text-center">
                                <div className={`text-[0.7rem] font-bold ${t.type === 'high' ? 'text-accent' : 'text-text-muted'}`}>
                                  {fmtTime(t.time)}
                                </div>
                                <div className="text-text-muted text-[0.6rem]">{t.height}m</div>
                                <div className="h-8 flex items-end mt-0.5">
                                  <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: `${Math.max(15, (t.height / 3) * 100)}%` }}
                                    transition={{ delay: i * 0.05 + j * 0.03, type: 'spring', stiffness: 100 }}
                                    className={`w-full rounded-sm ${t.type === 'high' ? 'bg-accent/40' : 'bg-accent/15'}`}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <p className="text-text-muted text-[0.65rem] text-center px-4">
                    ⚠️ Approximate predictions for planning only. Consult LINZ tide tables for navigation.
                  </p>
                </motion.div>
              )}

              {/* === FISHING TAB === */}
              {tab === 'fishing' && todayFishing && (
                <motion.div
                  key="fishing"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* Today's rating */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl bg-gradient-to-br from-surface-light to-card-light dark:from-surface dark:to-card border border-[rgba(16,42,66,0.09)] p-4 mb-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-text-muted text-[0.65rem] uppercase tracking-wider">Today's Rating</div>
                        <div className="font-display text-base font-bold">{todayFishing.ratingLabel}</div>
                      </div>
                      <RatingBadge rating={todayFishing.rating} label="" />
                    </div>

                    {/* Major periods */}
                    <div className="text-[0.65rem] font-bold text-text-secondary mb-1.5 uppercase tracking-wider">🐟 Major Feeding Times</div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {todayFishing.majorPeriods.map((p, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.1 + i * 0.1 }}
                          className="rounded-xl bg-accent/5 border border-accent/20 px-3 py-2"
                        >
                          <div className="font-bold text-sm">{p.start}</div>
                          <div className="text-text-muted text-[0.6rem]">to {p.end}</div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Minor periods */}
                    <div className="text-[0.65rem] font-bold text-text-secondary mb-1.5 uppercase tracking-wider">Minor Times</div>
                    <div className="grid grid-cols-2 gap-2">
                      {todayFishing.minorPeriods.map((p, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 + i * 0.1 }}
                          className="rounded-xl bg-surface-light dark:bg-surface border border-[rgba(79,179,217,0.08)] px-3 py-2"
                        >
                          <div className="font-semibold text-sm">{p.start}</div>
                          <div className="text-text-muted text-[0.6rem]">to {p.end}</div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>

                  {/* 7-day forecast */}
                  <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden mb-4">
                    <div className="bg-surface-light dark:bg-surface px-4 py-2.5 border-b border-[rgba(16,42,66,0.09)]">
                      <span className="font-display text-xs font-bold uppercase tracking-wider">7-Day Fishing Forecast</span>
                    </div>
                    <div className="divide-y divide-[rgba(79,179,217,0.05)]">
                      {data.fishingTimes.map((day, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.06 }}
                          className="px-3 py-2.5 flex items-center gap-3"
                        >
                          <div className="w-12 flex-shrink-0">
                            <div className="font-semibold text-xs">{i === 0 ? 'Today' : day.dayName.slice(0, 3)}</div>
                            <div className="text-text-muted text-[0.6rem]">{fmtDate(day.date)}</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[0.7rem] text-text-secondary truncate">
                              {day.majorPeriods.map(p => `${p.start}–${p.end}`).join(' · ')}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="text-xs">{RATING_STARS(day.rating)}</div>
                            <div className="text-text-muted text-[0.6rem]">{day.ratingLabel}</div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Info */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="rounded-xl bg-surface-light dark:bg-surface border border-[rgba(79,179,217,0.08)] p-4"
                  >
                    <h3 className="font-semibold text-xs mb-2">📖 How it works</h3>
                    <p className="text-text-secondary text-[0.7rem] leading-relaxed mb-1.5">
                      Fish feed most during <strong>major periods</strong> - when the moon is overhead or underfoot. <strong>Minor periods</strong> at moonrise/moonset are secondary windows.
                    </p>
                    <p className="text-text-secondary text-[0.7rem] leading-relaxed mb-1.5">
                      <strong>New & full moons</strong> = best fishing (5★). Quarter moons = moderate (3★).
                    </p>
                    <p className="text-text-secondary text-[0.7rem] leading-relaxed">
                      <strong>Pro tip:</strong> Fish an hour before/after tide changes during a major period for best results.
                    </p>
                  </motion.div>
                </motion.div>
              )}

              {/* === MOON TAB === */}
              {tab === 'moon' && (
                <motion.div
                  key="moon"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="rounded-2xl bg-gradient-to-br from-surface-light to-card-light dark:from-surface dark:to-card border border-[rgba(16,42,66,0.09)] p-6 text-center mb-4"
                  >
                    <MoonVisual phase={data.moonPhase.phase} illumination={data.moonPhase.illumination} />
                    <h2 className="font-display text-xl font-bold mt-3 mb-1">{data.moonPhase.name}</h2>
                    <div className="text-text-secondary text-xs mb-3">{data.moonPhase.illumination}% illuminated</div>
                    <div className="max-w-[250px] mx-auto">
                      <div className="h-2.5 rounded-full bg-surface dark:bg-surface-dark overflow-hidden border border-[rgba(16,42,66,0.09)]">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${data.moonPhase.illumination}%` }}
                          transition={{ duration: 1, type: 'spring', stiffness: 50 }}
                          className="h-full bg-gradient-to-r from-accent to-accent-bright"
                        />
                      </div>
                    </div>
                  </motion.div>

                  {/* Phase info cards */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {[
                      { icon: '🌑', name: 'New Moon', desc: 'Best fishing of the month. Fish feed aggressively. Target shallow water at dawn/dusk.' },
                      { icon: '🌓', name: 'First Quarter', desc: 'Moderate fishing. Focus on major solunar periods for best results.' },
                      { icon: '🌕', name: 'Full Moon', desc: 'Excellent - rivals new moon. Night fishing highly productive. Many species spawn.' },
                      { icon: '🌗', name: 'Last Quarter', desc: 'Moderate, similar to first quarter. Good daytime fishing during major periods.' },
                    ].map((p, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + i * 0.1 }}
                        whileTap={{ scale: 0.97 }}
                        className="rounded-xl bg-surface-light dark:bg-surface border border-[rgba(79,179,217,0.08)] p-3"
                      >
                        <div className="text-2xl mb-1">{p.icon}</div>
                        <div className="font-semibold text-xs mb-1">{p.name}</div>
                        <div className="text-text-secondary text-[0.65rem] leading-relaxed">{p.desc}</div>
                      </motion.div>
                    ))}
                  </div>

                  {/* 7-day moon ratings */}
                  <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden">
                    <div className="bg-surface-light dark:bg-surface px-4 py-2.5 border-b border-[rgba(16,42,66,0.09)]">
                      <span className="font-display text-xs font-bold uppercase tracking-wider">7-Day Moon & Rating</span>
                    </div>
                    <div className="divide-y divide-[rgba(79,179,217,0.05)]">
                      {data.tides.map((day, i) => {
                        const phase = data.fishingTimes[i];
                        return (
                          <div key={i} className="px-4 py-2.5 flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-xs">{i === 0 ? 'Today' : day.dayName}</div>
                              <div className="text-text-muted text-[0.6rem]">{fmtDate(day.date)}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs">{RATING_STARS(phase.rating)}</span>
                              <span className="text-text-muted text-[0.6rem]">{phase.ratingLabel}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          </div>{/* end mobile-only */}

          {/* === DESKTOP DASHBOARD === */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-[1fr_360px] gap-6 items-start">
              <div>
                <TideChart days={data.tides} />
                {todayTides && (
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {todayTides.tides.map((tide, i) => (
                      <TideCard key={i} tide={tide} index={i} />
                    ))}
                  </div>
                )}
                <SevenDayTides days={data.tides} />
                <p className="text-text-muted text-[0.65rem] px-1">
                  ⚠️ Approximate predictions for planning only. Consult LINZ tide tables for navigation.
                </p>
              </div>
              <div>
                <TideCountdown tides={data.tides} />
                <div className="rounded-2xl bg-gradient-to-br from-surface-light to-card-light dark:from-surface dark:to-card border border-[rgba(16,42,66,0.09)] p-4 mb-4 flex items-center gap-3">
                  <span className="text-3xl">{data.moonPhase.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-bold">{data.moonPhase.name}</div>
                    <div className="text-text-secondary text-xs">{data.moonPhase.illumination}% illuminated</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-text-muted text-[0.6rem] uppercase">Location</div>
                    <div className="font-bold text-xs">{data.port}</div>
                  </div>
                </div>
                {todayFishing && <FishingTodayCard fishing={todayFishing} />}
                <SevenDayFishing days={data.fishingTimes} />
              </div>
            </div>
          </div>

          {/* Fish guide cross-link */}
          <Link to="/fish-guide" className="mt-6 rounded-2xl border border-accent/25 bg-accent/5 p-4 flex items-center gap-4 hover:bg-accent/10 transition-colors group">
            <span className="text-3xl">🐟</span>
            <div className="flex-1">
              <p className="font-display font-extrabold text-sm group-hover:text-accent transition-colors">Know your limits — Fish ID chart &amp; legal catch limits</p>
              <p className="text-text-muted text-xs">Minimum sizes and daily bag limits for finfish and shellfish, by fishing area.</p>
            </div>
            <span className="text-accent font-bold">→</span>
          </Link>

          {/* Back link */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-text-muted text-xs hover:text-accent transition-colors">← Back to MarineLink</Link>
          </div>
        </div>
      )}
    </div>
  );
}
